import { useMemo } from 'react';

import dotize from 'dotize';
import { CircleAlertIcon, CircleCheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { cn } from '@/lib/utils';
import { CampaignSubscriber } from '@/shared/types/campaigns';

import { DEBT_SIDEBAR_DISPLAY_VARIABLES } from '../../../constants';
import { DraftCampaignSubscriber } from '../../../redux/actions';
import { selectCsvMappings, selectDraftSubscribers } from '../../../redux/selectors';
import { VariableDefinition } from '../../../types/column-mapping';
import { audienceNeedsResidenceState } from '../../../utils';
import { useUnmatchedFields } from '../../payment-recovery/hooks/useUnmatchedFields';

const getVariablePresence = (
  variable: VariableDefinition,
  subscriberKeys: Set<string>
): boolean => {
  if (variable.name === 'contact_info') {
    return subscriberKeys.has('email') || subscriberKeys.has('phone');
  }
  return subscriberKeys.has(variable.name);
};

interface PaymentRecoveryFilledContentProps {
  onMatchMissingFields?: () => void;
  onReviewAudience?: () => void;
  apiSubscribers?: CampaignSubscriber[];
  /** Whether any subscriber has a validation error. */
  hasErrors?: boolean;
  /** Hide the inline warning when a blocking form error (e.g. no valid rows) is already shown. */
  suppressWarning?: boolean;
}

export const PaymentRecoveryFilledContent = ({
  onMatchMissingFields,
  onReviewAudience,
  apiSubscribers,
  hasErrors,
  suppressWarning,
}: PaymentRecoveryFilledContentProps) => {
  const { t } = useTranslation();
  const draftSubscribers = useSelector(selectDraftSubscribers);

  const subscriberKeys = useMemo(() => {
    // Use API subscribers if Redux is empty (e.g., on page refresh)
    const subscribers = draftSubscribers.length > 0 ? draftSubscribers : apiSubscribers || [];
    const keys = new Set<string>();
    subscribers.forEach((subscriber: DraftCampaignSubscriber | CampaignSubscriber) => {
      if (subscriber.phone) {
        keys.add('phone');
      }
      if (subscriber.email) {
        keys.add('email');
      }

      if ('context' in subscriber && subscriber.context) {
        // API subscribers: flatten context to get dot-notation keys.
        // Orchestrator PR #1839 moved debt specs into `context.debts[]`, which
        // dotize expands to `debts.0.amount`, `debts.1.currency`, etc. The
        // sidebar presence check uses the canonical CSV variable names
        // (`debt.amount`, `debt.currency`, ...) so we fold any `debts.N.<field>`
        // hit back to the unindexed `debt.<field>` form.
        const flattened = dotize.convert(subscriber.context);
        Object.entries(flattened).forEach(([key, value]) => {
          if (!value) {
            return;
          }
          // dotize emits `debts[0].amount` (bracket form) for arrays; fold
          // any index back to the canonical unindexed `debt.<field>` form.
          // Same for `alternate_phones.<N>` / `alternate_emails.<N>` so the
          // sidebar checkmarks the bucket when any slot carries a value.
          const debtsMatch = key.match(/^debts\[\d+\]\.(.+)$/);
          if (debtsMatch) {
            keys.add(`debt.${debtsMatch[1]}`);
            return;
          }
          const altMatch = key.match(/^alternate_(phones|emails)\.\d+$/);
          if (altMatch) {
            keys.add(`alternate_${altMatch[1]}`);
            return;
          }
          keys.add(key);
        });
      } else {
        // Draft subscribers: direct properties. Fold slot-indexed multi-debt
        // keys (`debt.<N>.<field>`) back to the canonical `debt.<field>` so
        // the Required fields panel checkmarks Amount / Currency / etc.
        // regardless of how many debt slots the CSV carries — slot 0 is the
        // "main" debt by convention. Plain `debt.<field>` (single-debt CSV)
        // is preserved as-is by `keys.add(key)`.
        Object.entries(subscriber).forEach(([key, value]) => {
          if (!value || !String(value).trim() || key === 'error') {
            return;
          }
          keys.add(key);
          const slotted = key.match(/^debt\.\d+\.(.+)$/);
          if (slotted) {
            keys.add(`debt.${slotted[1]}`);
          }
          // `multi` targets (alternate_phones / alternate_emails) are stored
          // slot-indexed in Redux — fold them back to the bucket name so the
          // sidebar checks `alternate_phones` rather than `alternate_phones.0`.
          const altSlotted = key.match(/^alternate_(phones|emails)\.\d+$/);
          if (altSlotted) {
            keys.add(`alternate_${altSlotted[1]}`);
          }
        });
      }
    });
    return keys;
  }, [draftSubscribers, apiSubscribers]);

  const isDraft = draftSubscribers.length > 0;

  // residence_state is conditionally required (US/BR) — flip its flag so the sidebar shows it as Required.
  const needsResidenceState = useMemo(() => {
    const source = isDraft ? draftSubscribers : (apiSubscribers ?? []);
    return audienceNeedsResidenceState(source);
  }, [isDraft, draftSubscribers, apiSubscribers]);

  const csvMappings = useSelector(selectCsvMappings);
  const extraRequiredFields = useMemo(
    () => (needsResidenceState ? ['residence_state'] : []),
    [needsResidenceState]
  );
  // Required-field match = column mapped (same source as the Setup gate), not value-present; blank values are per-row errors.
  const { unmatchedFields } = useUnmatchedFields({
    csvMappings: csvMappings ?? null,
    isShowingReduxData: isDraft,
    extraRequiredFields,
  });
  const unmatchedNames = useMemo(
    () => new Set(unmatchedFields.map((f) => f.name)),
    [unmatchedFields]
  );

  const fieldStatuses = useMemo(
    () =>
      DEBT_SIDEBAR_DISPLAY_VARIABLES.map((variable) => {
        const required =
          variable.name === 'residence_state' && needsResidenceState ? true : variable.required;
        // contact_info (a display-only var) maps to the email/phone group.
        const mappingMatched =
          variable.name === 'contact_info'
            ? !(unmatchedNames.has('email') || unmatchedNames.has('phone'))
            : !unmatchedNames.has(variable.name);
        const hasData = getVariablePresence(variable, subscriberKeys);
        // Saved campaigns have no live mappings — fall back to data presence so a real gap still shows red.
        return { ...variable, required, hasData, isMatched: isDraft ? mappingMatched : hasData };
      }),
    [subscriberKeys, needsResidenceState, unmatchedNames, isDraft]
  );

  const hasMissingRequiredFields = fieldStatuses.some((f) => f.required && !f.isMatched);

  return (
    <div className="flex flex-col gap-4">
      {/* Required fields */}
      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium text-muted-foreground">
          {t('campaigns.setup.required_fields')}
        </p>

        {fieldStatuses
          .filter((f) => f.required)
          .map((field) => (
            <div key={field.name} className="flex items-center gap-1">
              {field.isMatched ? (
                <CircleCheckIcon className="size-4 shrink-0 text-success" />
              ) : (
                <CircleAlertIcon className="size-4 shrink-0 text-destructive" />
              )}
              <span
                className={cn(
                  'text-sm font-medium',
                  field.isMatched ? 'text-muted-foreground' : 'text-destructive'
                )}
              >
                {field.label}
              </span>
            </div>
          ))}
      </div>

      {/* Optional fields — only the ones we actually have data for. */}
      {fieldStatuses.some((f) => !f.required && f.hasData) && (
        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium text-muted-foreground">
            {t('campaigns.setup.optional_fields')}
          </p>

          {fieldStatuses
            .filter((f) => !f.required && f.hasData)
            .map((field) => (
              <div key={field.name} className="flex items-center gap-1">
                <CircleCheckIcon className="size-4 shrink-0 text-success" />
                <span className="text-sm font-medium text-muted-foreground">{field.label}</span>
              </div>
            ))}
        </div>
      )}

      {/* Action links */}
      <div className="flex items-center gap-1.5">
        {onReviewAudience && (
          <button
            type="button"
            className="text-sm font-medium text-primary hover:underline"
            onClick={onReviewAudience}
          >
            {t('campaigns.setup.review_audience')}
          </button>
        )}
        {onReviewAudience && onMatchMissingFields && (
          <span className="text-sm font-medium text-muted-foreground">·</span>
        )}
        {onMatchMissingFields && (
          <button
            type="button"
            className="text-sm font-medium text-primary hover:underline"
            onClick={onMatchMissingFields}
          >
            {t('campaigns.setup.match_fields')}
          </button>
        )}
      </div>

      {!suppressWarning && hasMissingRequiredFields && (
        <p className="text-sm text-warning">{t('campaigns.setup.unmatched_required_detected')}</p>
      )}
      {!suppressWarning && !hasMissingRequiredFields && hasErrors && (
        <p className="text-sm text-warning">{t('campaigns.setup.some_invalid_subscribers')}</p>
      )}
    </div>
  );
};
